"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { setPackProgress } from "@/lib/actions/packs";
import { checklistLines, KIND_META, isContentKind } from "@/lib/packs";
import type { PackDetail, ResolvedPackItem } from "@/lib/packQueries";

/* W10 Context Packs — consumer view. Work through curated content in reading
 * order; done/skip is tracked per signed-in reader. Staleness on live items is
 * surfaced honestly; private content the reader can't access never appears
 * (RLS filters it before it reaches here). */

function StaleChip({ item }: { item: ResolvedPackItem }) {
  if (item.staleness.kind === "source_updated")
    return <Chip tone="warn">Source updated since review</Chip>;
  if (item.staleness.kind === "newer_edition")
    return <Chip tone="muted">Newer edition available</Chip>;
  return null;
}

function Chip({ children, tone = "muted" }: { children: React.ReactNode; tone?: "warn" | "muted" | "copper" }) {
  const tones = {
    warn: { bg: "#a4442c14", fg: "#a4442c" },
    muted: { bg: "var(--surface-alt2)", fg: "var(--muted)" },
    copper: { bg: "var(--surface-alt2)", fg: "var(--copper)" },
  }[tone];
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold tracking-wide"
      style={{ background: tones.bg, color: tones.fg }}
    >
      {children}
    </span>
  );
}

function ContentItem({
  item,
  signedIn,
  onProgress,
}: {
  item: ResolvedPackItem;
  signedIn: boolean;
  onProgress: (status: "done" | "skipped" | null) => void;
}) {
  const c = item.content;
  const done = item.progress === "done";
  const skipped = item.progress === "skipped";
  return (
    <div
      className="rounded-[14px] border border-border-soft bg-surface p-4 transition-opacity"
      style={{ opacity: skipped ? 0.6 : 1 }}
    >
      <div className="flex items-start gap-3">
        {signedIn && (
          <button
            aria-label={done ? "Mark not done" : "Mark done"}
            onClick={() => onProgress(done ? null : "done")}
            className="mt-0.5 grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full border transition-colors"
            style={{
              borderColor: done ? "var(--green)" : "var(--border)",
              background: done ? "var(--green)" : "transparent",
              color: "var(--surface)",
            }}
          >
            {done ? "✓" : ""}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10.5px] font-bold uppercase tracking-[.1em] text-copper">
              {KIND_META[item.kind].label}
            </span>
            {item.refMode === "pinned" && (
              <Chip tone="muted">Pinned{c?.pinnedVersion ? ` · ${c.pinnedVersion}` : ""}</Chip>
            )}
            {c?.flags.map((f) => (
              <Chip key={f} tone="warn">
                {f}
              </Chip>
            ))}
            <StaleChip item={item} />
          </div>

          {!item.available || !c ? (
            <p className="mt-1 text-[13.5px] italic text-muted">
              This content is no longer available.
            </p>
          ) : (
            <>
              <div className="mt-1 font-serif text-[16.5px] font-medium leading-snug text-ink">
                {c.href ? (
                  <Link href={c.href} className="hover:text-copper">
                    {c.title}
                  </Link>
                ) : (
                  c.title
                )}
              </div>
              {c.subtitle && <div className="mt-0.5 text-[12.5px] text-muted-2">{c.subtitle}</div>}
              {c.detail && (
                <p className="mt-1.5 whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink-3 line-clamp-4">
                  {c.detail}
                </p>
              )}
              {c.bookTitle && (
                <div className="mt-2 text-[12px] text-muted-2">
                  from <span className="font-medium text-ink-3">{c.bookTitle}</span>
                </div>
              )}
            </>
          )}

          {item.blurb && (
            <p className="mt-2.5 border-l-2 pl-3 text-[13px] italic leading-relaxed text-ink-3" style={{ borderColor: "var(--copper)" }}>
              {item.blurb}
            </p>
          )}

          {signedIn && (
            <div className="mt-2.5 flex gap-3 text-[12px] font-semibold">
              {!done && (
                <button onClick={() => onProgress(skipped ? null : "skipped")} className="text-muted-2 hover:text-ink-3">
                  {skipped ? "Un-skip" : "Skip"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StructuralItem({
  item,
  signedIn,
  onProgress,
}: {
  item: ResolvedPackItem;
  signedIn: boolean;
  onProgress: (status: "done" | "skipped" | null) => void;
}) {
  const done = item.progress === "done";
  if (item.kind === "section") {
    return (
      <div className="pt-4">
        <h3 className="font-serif text-[20px] font-semibold text-ink">{item.title || "Section"}</h3>
        {item.blurb && <p className="mt-1 text-[13.5px] text-ink-3">{item.blurb}</p>}
      </div>
    );
  }
  if (item.kind === "link") {
    return (
      <div className="rounded-[14px] border border-border-soft bg-surface p-4">
        <span className="text-[10.5px] font-bold uppercase tracking-[.1em] text-copper">Link</span>
        <div className="mt-1">
          <a href={item.url ?? "#"} target="_blank" rel="noopener noreferrer" className="font-serif text-[16px] font-medium text-ink hover:text-copper">
            {item.title || item.url}
          </a>
        </div>
        {item.blurb && <p className="mt-1.5 text-[13px] italic text-ink-3">{item.blurb}</p>}
      </div>
    );
  }
  const isChecklist = item.kind === "checklist";
  const lines = isChecklist ? checklistLines(item.body) : [];
  const tone = item.kind === "access" ? { bg: "var(--surface-alt2)", label: "Access" } : { bg: "var(--surface)", label: KIND_META[item.kind].label };
  return (
    <div className="rounded-[14px] border border-border-soft p-4" style={{ background: tone.bg }}>
      <div className="flex items-start gap-3">
        {signedIn && (
          <button
            aria-label={done ? "Mark not done" : "Mark done"}
            onClick={() => onProgress(done ? null : "done")}
            className="mt-0.5 grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full border transition-colors"
            style={{ borderColor: done ? "var(--green)" : "var(--border)", background: done ? "var(--green)" : "transparent", color: "var(--surface)" }}
          >
            {done ? "✓" : ""}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <span className="text-[10.5px] font-bold uppercase tracking-[.1em] text-copper">{tone.label}</span>
          {item.title && <div className="mt-1 font-serif text-[16px] font-medium text-ink">{item.title}</div>}
          {isChecklist ? (
            <ul className="mt-1.5 space-y-1">
              {lines.map((l, i) => (
                <li key={i} className="flex gap-2 text-[13.5px] text-ink-3">
                  <span className="text-muted-2">□</span>
                  {l}
                </li>
              ))}
            </ul>
          ) : (
            item.body && <p className="mt-1.5 whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink-3">{item.body}</p>
          )}
          {item.blurb && <p className="mt-2 text-[13px] italic text-ink-3">{item.blurb}</p>}
        </div>
      </div>
    </div>
  );
}

export function PackView({ pack, signedIn }: { pack: PackDetail; signedIn: boolean }) {
  const [items, setItems] = useState(pack.items);

  const trackable = useMemo(() => items.filter((i) => i.kind !== "section"), [items]);
  const doneCount = trackable.filter((i) => i.progress === "done").length;
  const pct = trackable.length ? Math.round((doneCount / trackable.length) * 100) : 0;

  async function progress(item: ResolvedPackItem, status: "done" | "skipped" | null) {
    // optimistic
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, progress: status } : i)));
    const res = await setPackProgress(pack.id, item.id, status);
    if (!res.ok) {
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, progress: item.progress } : i)));
    }
  }

  return (
    <div className="mx-auto max-w-[860px] px-[var(--gutter)] py-10 pb-24">
      <div className="flex items-center gap-3">
        <Link href="/packs" className="text-[13px] font-semibold text-muted-2 hover:text-copper">
          ← Packs
        </Link>
        {pack.isOwner && (
          <Link href={`/packs/${pack.slug}/edit`} className="ml-auto text-[13px] font-bold text-copper hover:underline">
            Edit pack
          </Link>
        )}
      </div>

      <span className="mt-5 block text-[11px] font-bold uppercase tracking-[.14em] text-copper">Context Pack</span>
      <h1 className="mt-1 font-serif text-[32px] font-semibold leading-tight tracking-[-.02em] text-ink">
        {pack.title}
      </h1>
      {pack.purpose && <p className="mt-2 max-w-[46em] text-[15px] leading-relaxed text-ink-3">{pack.purpose}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-2">
        {pack.audience && <span>For {pack.audience}</span>}
        {pack.ownerName && (
          <span>
            Maintained by{" "}
            {pack.ownerHandle ? (
              <Link href={`/authors/${pack.ownerHandle}`} className="text-ink-3 hover:text-copper">
                {pack.ownerName}
              </Link>
            ) : (
              pack.ownerName
            )}
          </span>
        )}
        {pack.reviewDate && (
          <span style={{ color: pack.pastReviewDate ? "#a4442c" : undefined }}>
            {pack.pastReviewDate ? "Past its review date" : "Review by"} {pack.reviewDate}
          </span>
        )}
      </div>

      {pack.anySourceUpdated && (
        <div className="mt-4 rounded-[12px] border px-4 py-3 text-[13px]" style={{ borderColor: "#a4442c40", background: "#a4442c0d", color: "#a4442c" }}>
          Some live items reference books that have changed since the maintainer last reviewed this
          pack — details may be out of date.
        </div>
      )}

      {pack.prerequisites && (
        <div className="mt-5 rounded-[12px] border border-border-soft bg-surface-alt2 px-4 py-3">
          <div className="text-[11px] font-bold uppercase tracking-[.1em] text-copper">Prerequisites</div>
          <p className="mt-1 whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink-3">{pack.prerequisites}</p>
        </div>
      )}

      {trackable.length > 0 && (
        <div className="mt-6 flex items-center gap-3">
          <div className="h-[6px] max-w-[360px] flex-1 overflow-hidden rounded-full bg-surface-alt2">
            <div className="h-full rounded-full bg-copper" style={{ width: `${Math.max(2, pct)}%` }} />
          </div>
          <span className="text-[12px] font-semibold text-muted-2">
            {doneCount}/{trackable.length} done
          </span>
        </div>
      )}
      {!signedIn && (
        <p className="mt-3 text-[13px] text-muted">
          <Link href={`/signin?next=/packs/${pack.slug}`} className="font-semibold text-copper hover:underline">
            Sign in
          </Link>{" "}
          to track your progress through this pack.
        </p>
      )}

      <div className="mt-7 space-y-3.5">
        {items.length === 0 ? (
          <p className="text-[14px] text-muted">This pack has no items yet.</p>
        ) : (
          items.map((item) =>
            isContentKind(item.kind) ? (
              <ContentItem key={item.id} item={item} signedIn={signedIn} onProgress={(s) => progress(item, s)} />
            ) : (
              <StructuralItem key={item.id} item={item} signedIn={signedIn} onProgress={(s) => progress(item, s)} />
            ),
          )
        )}
      </div>
    </div>
  );
}
